/**
 * Access control for the Platform area — the cross-company admin pages that
 * operate on *other* companies' data (extending Shopify trials, global settings).
 *
 * Mirrors the backend guard: the caller must belong to the Neatolabs company AND
 * hold the `admin` role. Auth0 roles are global across companies, so the role
 * alone is not enough — every customer's own admin carries it too.
 *
 * This gate is UX only: it decides what renders. The backend holds the real
 * check (company id from GCP Secret Manager + role, read off the token), because
 * anything decided here can be bypassed from devtools.
 */

// Auth0 role that grants access, checked together with the company below.
export const PLATFORM_ADMIN_ROLE = (import.meta.env.VITE_PLATFORM_ADMIN_ROLE || 'admin').trim().toLowerCase()

// Neatolabs' own company id. Not a credential — it identifies which company the
// user must belong to, and knowing it grants nothing, because the backend reads
// company membership off the signed token rather than trusting anything the
// client sends. The authoritative copy stays in GCP Secret Manager.
export const PLATFORM_COMPANY_ID = (import.meta.env.VITE_PLATFORM_COMPANY_ID || '').trim().toLowerCase()

export const PLATFORM_ROUTE = '/platform'

interface PlatformUser {
  roles?: readonly (string | null | undefined)[] | null
  companies?: readonly ({ id?: string | null } | null)[] | null
}

const normalize = (value: unknown) => (typeof value === 'string' ? value.trim().toLowerCase() : '')

let hasWarnedAboutMissingCompanyId = false

const isPlatformAdmin = (user?: PlatformUser | null): boolean => {
  if (!user) return false

  // Fail closed when unconfigured: without the company id the role check alone
  // would let every customer admin in.
  if (!PLATFORM_COMPANY_ID) {
    if (import.meta.env.DEV && !hasWarnedAboutMissingCompanyId) {
      hasWarnedAboutMissingCompanyId = true
      console.warn('VITE_PLATFORM_COMPANY_ID is not set — the Platform area is hidden for everyone.')
    }
    return false
  }

  const hasRole = Array.isArray(user.roles) && user.roles.some((role) => normalize(role) === PLATFORM_ADMIN_ROLE)
  if (!hasRole) return false

  // Membership, not the selected company — a Neatolabs admin who is currently
  // browsing a customer's store still belongs to Neatolabs, which is exactly
  // what the backend checks.
  return (
    Array.isArray(user.companies) && user.companies.some((company) => normalize(company?.id) === PLATFORM_COMPANY_ID)
  )
}

export default isPlatformAdmin
